/**
 * The window a backtest runs over (roadmap B1), drawn as Explore draws its own:
 * the `.date-preset` chips (7d 1M 3M 6M 1Y), **More** with rarer rolling and
 * calendar windows, two UTC date fields - and **All time**, which is held, as
 * `hold-switch` is held, because it reads everything the fleet has.
 * Sketch: `docs/design/sketches/argus-quicktest-v2.html`.
 *
 * Windows go to the API as `range_from` / `range_to` (UTC; migration 0045),
 * computed from Explore's own `presetWindow` where Explore has the preset. All
 * time goes as `all_time` with no window: the watcher finds where the data
 * begins (migration 0046).
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { presetWindow } from '../../utils/dates.ts';

export type WindowPreset =
  | '7d' | '14d' | '1mo' | '2m' | '3m' | '6m' | '9m' | '1y' | '18m'
  | 'mtd' | 'prevmonth' | 'qtd' | 'prevq' | 'ytd' | 'prevyear';

export type BacktestWindow =
  | { kind: 'preset'; id: WindowPreset }
  | { kind: 'dates'; from: string; to: string }
  | { kind: 'all' };

export const DEFAULT_WINDOW: BacktestWindow = { kind: 'preset', id: '3m' };

const DAY = 86400_000;
/** A window of more days than this is heavy, as All time is: said beside Find. */
export const HEAVY_DAYS = 400;
/** The longest chosen window (backend `MAX_DAYS`). */
const MAX_DAYS = 3650;
/** How long All time is held. A beat longer than hold-switch's 900 ms: it costs more. */
export const HOLD_MS = 1200;

const CHIPS: ReadonlyArray<[WindowPreset, string, string]> = [
  ['7d', '7d', 'Last 7 days'],
  ['1mo', '1M', 'Last month'],
  ['3m', '3M', 'Last 3 months'],
  ['6m', '6M', 'Last 6 months'],
  ['1y', '1Y', 'Last year'],
];
const ROLLING_MORE: ReadonlyArray<[WindowPreset, string]> = [
  ['14d', 'Last 14 days'],
  ['2m', 'Last 2 months'],
  ['9m', 'Last 9 months'],
  ['18m', 'Last 18 months'],
];
const CALENDAR: ReadonlyArray<[string, [WindowPreset, string], [WindowPreset, string]]> = [
  ['Month', ['mtd', 'Month to date'], ['prevmonth', 'Previous month']],
  ['Quarter', ['qtd', 'Quarter to date'], ['prevq', 'Previous quarter']],
  ['Year', ['ytd', 'Year to date'], ['prevyear', 'Previous year']],
];
/** Presets under More: the More chip wears the active one's name, as Explore's does. */
const MORE_LABELS: Partial<Record<WindowPreset, string>> = {
  '14d': '14d', '2m': '2M', '9m': '9M', '18m': '18M',
  mtd: 'MTD', prevmonth: 'Prev month', qtd: 'QTD', prevq: 'Prev quarter', ytd: 'YTD', prevyear: 'Prev year',
};
const TITLES: Record<WindowPreset, string> = Object.fromEntries([
  ...CHIPS.map(([id, , title]) => [id, title]),
  ...ROLLING_MORE,
  ...CALENDAR.flatMap(([, cur, prev]) => [cur, prev]),
]) as Record<WindowPreset, string>;

const isoDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** Times without a zone are UTC (Explore's picker values, the watcher's times). */
function utcMs(value: string): number {
  return Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value.length === 10 ? `${value}T00:00` : value}Z`);
}

/** [start, end) in UTC milliseconds; null for All time. */
export function windowRange(value: BacktestWindow, now: Date = new Date()): [number, number] | null {
  if (value.kind === 'all') return null;
  if (value.kind === 'dates') return [utcMs(value.from), utcMs(value.to) + DAY];
  const explore = presetWindow(value.id, now);
  if (explore) return [utcMs(explore.start), utcMs(explore.end)];
  // The windows Explore does not offer, on its terms: whole UTC days, ending at
  // midnight tomorrow so today counts.
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const end = today + DAY;
  const monthsBack = (n: number): number => {
    const d = new Date(end);
    d.setUTCMonth(d.getUTCMonth() - n);
    return d.getTime();
  };
  const q = Math.floor(now.getUTCMonth() / 3) * 3;
  const quarter = Date.UTC(now.getUTCFullYear(), q, 1);
  switch (value.id) {
    case '14d': return [end - 14 * DAY, end];
    case '2m': return [monthsBack(2), end];
    case '9m': return [monthsBack(9), end];
    case '18m': return [monthsBack(18), end];
    case 'qtd': return [quarter, end];
    case 'prevq': return [Date.UTC(now.getUTCFullYear(), q - 3, 1), quarter];
    default: return [monthsBack(3), end];
  }
}

/** Whole days the window covers; null for All time. */
export function windowDays(value: BacktestWindow): number | null {
  const range = windowRange(value);
  return range ? Math.round((range[1] - range[0]) / DAY) : null;
}

export function windowIsHeavy(value: BacktestWindow): boolean {
  return value.kind === 'all' || (windowDays(value) ?? 0) > HEAVY_DAYS;
}

/** Why the window cannot run, or null. */
export function windowProblem(value: BacktestWindow): string | null {
  if (value.kind !== 'dates') return null;
  const range = windowRange(value)!;
  if (!Number.isFinite(range[0]) || !Number.isFinite(range[1])) return 'Choose both dates.';
  if (range[1] <= range[0]) return 'The last day is before the first.';
  if (range[0] > Date.now()) return 'The window starts in the future.';
  if ((range[1] - range[0]) / DAY > MAX_DAYS) return `A window is at most ${MAX_DAYS} days. Hold All time for more.`;
  return null;
}

/** The request's part for this window. */
export function windowRequest(
  value: BacktestWindow,
): { range_from: string; range_to: string } | { all_time: true } {
  const range = windowRange(value);
  if (!range) return { all_time: true };
  return { range_from: new Date(range[0]).toISOString(), range_to: new Date(range[1]).toISOString() };
}

/** The same window as a key, to tell one question from another. */
export function windowKey(value: BacktestWindow): string {
  return value.kind === 'preset' ? value.id : value.kind === 'all' ? 'all' : `${value.from}..${value.to}`;
}

/** A run's own window, to put back on screen when the run is opened again. */
export function windowOfRun(run: {
  range_from: string | null;
  range_to: string | null;
  days: number;
  all_time?: boolean;
  result?: { range_from: string; range_to: string } | null;
}): BacktestWindow {
  if (run.all_time) return { kind: 'all' };
  if (run.range_from && run.range_to) {
    return { kind: 'dates', from: isoDay(utcMs(run.range_from)), to: isoDay(utcMs(run.range_to) - 1) };
  }
  // "The last N days" from before windows were chosen (migration 0045): the
  // preset of that length, or else the dates the run really read.
  const id = ({ 7: '7d', 14: '14d' } as Record<number, WindowPreset>)[run.days];
  if (id) return { kind: 'preset', id };
  if (run.result?.range_from && run.result.range_to) {
    return { kind: 'dates', from: isoDay(utcMs(run.result.range_from)), to: isoDay(utcMs(run.result.range_to)) };
  }
  return DEFAULT_WINDOW;
}

function day(iso: string): string {
  return new Date(utcMs(iso)).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** The window a run covers, in words: "All time", its dates, or "Last 90 days". */
export function windowWords(run: { range_from: string | null; range_to: string | null; days: number; all_time?: boolean }): string {
  if (run.all_time) return 'All time';
  if (run.range_from && run.range_to) return `${day(run.range_from)} to ${day(new Date(utcMs(run.range_to) - 1).toISOString())}`;
  return `Last ${run.days} days`;
}

/** The short name of the window on screen: "Last 3 months", "All time", or its dates. */
export function windowTitle(value: BacktestWindow): string {
  if (value.kind === 'preset') return TITLES[value.id];
  if (value.kind === 'all') return 'All time';
  return `${day(value.from)} to ${day(value.to)}`;
}

/**
 * The picker. Fires `window-change` with the new `BacktestWindow`. All time is
 * held: the warning shows as the hold starts; letting go early, or a plain
 * click, chooses nothing and says how.
 */
@customElement('argus-window-pick')
export class ArgusWindowPick extends LightElement {
  @property({ attribute: false }) value: BacktestWindow = DEFAULT_WINDOW;
  /** Entities the run will read, for the warning: "on 11 ships". */
  @property({ attribute: false }) entities: number | null = null;
  @property() noun = 'ship';

  @state() private moreOpen = false;
  @state() private holding = false;
  /** The All time note: while holding, after a short press, or once chosen. */
  @state() private note: 'holding' | 'short' | 'chosen' | null = null;
  private holdTimer: number | undefined;
  private noteTimer: number | undefined;
  private holdStart = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('pointerdown', this.onOutside, true);
    document.addEventListener('pointerup', this.onRelease);
    document.addEventListener('pointercancel', this.onRelease);
  }

  override disconnectedCallback(): void {
    document.removeEventListener('pointerdown', this.onOutside, true);
    document.removeEventListener('pointerup', this.onRelease);
    document.removeEventListener('pointercancel', this.onRelease);
    window.clearTimeout(this.holdTimer);
    window.clearTimeout(this.noteTimer);
    super.disconnectedCallback();
  }

  private onOutside = (e: Event): void => {
    if (this.moreOpen && !(e.target as Element | null)?.closest('.preset-more-wrap')) this.moreOpen = false;
  };

  private onRelease = (): void => this.release();

  private choose(next: BacktestWindow): void {
    this.moreOpen = false;
    this.dispatchEvent(new CustomEvent<BacktestWindow>('window-change', { detail: next, bubbles: true }));
  }

  private say(note: 'short' | 'chosen' | null, ms = 3200): void {
    window.clearTimeout(this.noteTimer);
    this.note = note;
    if (note) this.noteTimer = window.setTimeout(() => (this.note = null), ms);
  }

  private press(): void {
    if (this.holding) return;
    this.holding = true;
    this.holdStart = performance.now();
    window.clearTimeout(this.noteTimer);
    this.note = 'holding';
    this.holdTimer = window.setTimeout(() => {
      this.holding = false;
      this.holdTimer = undefined;
      this.choose({ kind: 'all' });
      this.say('chosen', 2600);
    }, HOLD_MS);
  }

  private release(): void {
    if (!this.holding) return;
    window.clearTimeout(this.holdTimer);
    this.holdTimer = undefined;
    this.holding = false;
    this.say('short');
  }

  private chip(id: WindowPreset, label: string, title: string): TemplateResult {
    const on = this.value.kind === 'preset' && this.value.id === id;
    return html`<button
      type="button"
      class="date-preset ${on ? 'active' : ''}"
      title=${title}
      aria-pressed=${on ? 'true' : 'false'}
      @click=${() => this.choose({ kind: 'preset', id })}
    >
      ${label}
    </button>`;
  }

  private renderMore(): TemplateResult {
    const active = this.value.kind === 'preset' ? MORE_LABELS[this.value.id] : undefined;
    const item = (id: WindowPreset, label: string): TemplateResult => {
      const on = this.value.kind === 'preset' && this.value.id === id;
      return html`<button
        type="button"
        class="preset-more-item ${on ? 'active' : ''}"
        role="menuitemradio"
        aria-checked=${on ? 'true' : 'false'}
        @click=${() => this.choose({ kind: 'preset', id })}
      >
        ${label}
      </button>`;
    };
    return html`<span class="preset-more-wrap">
      <button
        type="button"
        class="date-preset preset-more-btn ${active ? 'active' : ''} ${this.moreOpen ? 'is-open' : ''}"
        aria-haspopup="menu"
        aria-expanded=${this.moreOpen ? 'true' : 'false'}
        title=${active && this.value.kind === 'preset' ? TITLES[this.value.id] : 'More windows'}
        @click=${() => (this.moreOpen = !this.moreOpen)}
      >
        ${active ?? 'More'}<span class="preset-more-caret" aria-hidden="true"></span>
      </button>
      ${this.moreOpen
        ? html`<div class="preset-more-menu" role="menu">
            <span class="preset-more-head">Rolling</span>
            <div class="argus-window-rolling">${ROLLING_MORE.map(([id, label]) => item(id, label))}</div>
            <div class="preset-more-sep"></div>
            <div class="preset-more-grid">
              <span class="preset-more-head">Calendar</span>
              <span class="preset-more-col">This</span>
              <span class="preset-more-col">Previous</span>
              ${CALENDAR.map(
                ([row, cur, prev]) => html`<span class="preset-more-row">${row}</span>${item(...cur)}${item(...prev)}`,
              )}
            </div>
          </div>`
        : nothing}
    </span>`;
  }

  private renderAllTime(): TemplateResult {
    const on = this.value.kind === 'all';
    return html`<span class="argus-window-all">
      <button
        type="button"
        class="date-preset argus-hold-chip ${on ? 'active' : ''} ${this.holding ? 'is-holding' : ''}"
        style=${`--hold-ms:${HOLD_MS}ms`}
        aria-pressed=${on ? 'true' : 'false'}
        aria-describedby="argus-window-all-note"
        title="Hold to choose All time"
        @pointerdown=${(e: PointerEvent) => {
          if (e.button === 0) this.press();
        }}
        @pointerleave=${() => this.release()}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key !== ' ' && e.key !== 'Enter') return;
          e.preventDefault();
          if (!e.repeat) this.press();
        }}
        @keyup=${(e: KeyboardEvent) => {
          if (e.key === ' ' || e.key === 'Enter') this.release();
        }}
        @click=${(e: Event) => e.preventDefault()}
      >
        <span>All time</span>
      </button>
      ${this.note
        ? html`<div class="argus-hold-warn" id="argus-window-all-note" role="status">
            <b>All time is heavy</b>
            <p>
              It reads everything the data has${this.entities ? ` on ${this.entities} ${this.noun}${this.entities === 1 ? '' : 's'}` : ''},
              from where it begins. It can take several minutes, and other backtests wait while the
              watcher works on it.
            </p>
            <p class="argus-hold-say">
              ${this.note === 'holding'
                ? 'Keep holding to choose it…'
                : this.note === 'chosen'
                  ? 'Chosen. Press Find episodes when you are ready.'
                  : 'Hold the button to choose All time.'}
            </p>
          </div>`
        : nothing}
    </span>`;
  }

  override render(): TemplateResult {
    const range = windowRange(this.value);
    const from = range ? isoDay(range[0]) : '';
    const to = range ? isoDay(range[1] - DAY) : '';
    const today = isoDay(Date.now());
    const problem = windowProblem(this.value);
    const setDate = (which: 'from' | 'to', v: string): void => {
      if (!v) return;
      this.choose({ kind: 'dates', from: which === 'from' ? v : from || v, to: which === 'to' ? v : to || v });
    };
    return html`<div class="argus-window">
      <div class="date-presets argus-window-chips" role="group" aria-label="Window">
        ${CHIPS.map(([id, label, title]) => this.chip(id, label, title))} ${this.renderMore()} ${this.renderAllTime()}
      </div>
      <div class="argus-window-dates">
        <input
          type="date"
          aria-label="From"
          .value=${from}
          max=${today}
          ?disabled=${this.value.kind === 'all'}
          @change=${(e: Event) => setDate('from', (e.target as HTMLInputElement).value)}
        />
        <span class="argus-lfaint">to</span>
        <input
          type="date"
          aria-label="To"
          .value=${to}
          max=${today}
          ?disabled=${this.value.kind === 'all'}
          @change=${(e: Event) => setDate('to', (e.target as HTMLInputElement).value)}
        />
        <span class="utc-badge" title="All times are UTC">UTC</span>
      </div>
      ${problem ? html`<p class="argus-form-error" role="alert">${problem}</p>` : nothing}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-window-pick': ArgusWindowPick;
  }
}
